<script setup lang="ts" generic="T extends ListItem = ListItem">
import type { ListProps, ListSlots } from '#layers/ui/components/List/List.vue';
import type { ListItem } from '#layers/ui/components/List/ListItem.vue';
import { reactivePick } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import { computed, nextTick, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import DAnimatedSheet from '#layers/ui/components/AnimatedSheet.vue';
import DList from '#layers/ui/components/List/List.vue';
import { mergeUi } from '#layers/ui/utils/merge-ui';

defineOptions({
	name: 'ListCard',
});

const props = defineProps<ListCardProps<T>>();

const emit = defineEmits<{
	select: [item: T, event?: Event];
	reorder: [event: { item: T; oldIndex: number; newIndex: number; items: T[] }];
}>();

const slots = defineSlots<ListCardSlots<T>>();

interface ListCardProps<T extends ListItem> extends Partial<Pick<ListProps<T>, 'disabled' | 'selectable' | 'sortable' | 'allowDragOnlyOnHandle'>> {
	ui?: Partial<ListProps<T>['ui'] & { header?: string }>;
}

interface ListCardSlots<T extends ListItem> extends Pick<ListSlots<T>, 'item-content' | 'item-trailing' | 'item-leading' | 'item-label' | 'item-description'> {
	'header'?: () => any;
	'header-actions'?: () => any;
	'footer'?: () => any;
}

const items = defineModel<T[]>('items', { required: true });

const listSlotNames = computed(() =>
	([
		'item-content',
		'item-trailing',
		'item-leading',
		'item-label',
		'item-description',
	] as const).filter((name) => slots[name]),
);

const forwarded = useForwardPropsEmits(reactivePick(props, 'disabled', 'selectable', 'sortable', 'allowDragOnlyOnHandle'), emit);

const hasHeader = computed(() => slots['header']?.().length > 0);
const hasHeaderActions = computed(() => slots['header-actions']?.().length > 0);

const listUi = computed(() =>
	mergeUi(props.ui, {
		root: 'border-none',
		item: {
			base: 'hover:bg-muted',
		},
	}),
);

const { t } = useI18n();

const scroller = useTemplateRef('scroller');

async function scrollToTop() {
	await nextTick();

	if (scroller.value) {
		scroller.value.scrollTop = 0;
	}
}

async function scrollToBottom() {
	await nextTick();

	if (scroller.value) {
		scroller.value.scrollTop = scroller.value.scrollHeight;
	}
}

defineExpose({
	scrollToTop,
	scrollToBottom,
});
</script>

<template>
	<DAnimatedSheet dimension="height" class="flex flex-col divide-y overflow-hidden rounded-md border">
		<div
			v-if="hasHeader || hasHeaderActions"
			class="flex h-13 flex-none items-center justify-between gap-x-3 px-6"
		>
			<div class="truncate text-sm font-medium text-default" :class="props.ui?.header">
				<!-- Gate the fallback on the slot's presence, not its output: Vue treats a comment-only
				slot (a consumer's `v-if` that is false) as empty, which would render the fallback. -->
				<slot v-if="slots.header" name="header" />
				<span v-else-if="items.length === 0">
					{{ t('common.no_items') }}
				</span>
			</div>
			<slot name="header-actions" />
		</div>
		<div
			v-if="items.length > 0"
			ref="scroller"
			class="flex-1 overflow-auto rounded-none border-x-0 border-t-0"
		>
			<DList
				v-bind="forwarded"
				v-model:items="items"
				:ui="listUi"
			>
				<template v-for="name in listSlotNames" #[name]="slotData: any">
					<slot :name v-bind="slotData" />
				</template>
			</DList>
		</div>
		<slot name="footer" />
	</DAnimatedSheet>
</template>
